import type { CSSProperties, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import type { InputProps } from "@/components/ui/input";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/lib";

interface DollarInputProps extends InputProps {
  name?: string;
  suffix?: ReactNode;
  onMaxSelected?: () => void;
  suffixPadding?: number;
  suffixClassName?: string;
  className?: string;
}

const DEFAULT_SUFFIX_PADDING = 30;
const MIN_SUFFIX_PADDING = 28;
const SUFFIX_PADDING_PER_CHARACTER = 14;
const PREFIX_GAP = 8;
const PREFIX_INSET = 12;

const CurrencyInput = ({
  name,
  suffix = "",
  suffixClassName,
  onMaxSelected,
  suffixPadding,
  className,
  value,
  ...rest
}: DollarInputProps) => {
  const formCtx = useFormContext();
  const prefixRef = useRef<HTMLSpanElement>(null);
  const [prefixWidth, setPrefixWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!suffix || !prefixRef.current) {
      setPrefixWidth(null);
      return;
    }

    const element = prefixRef.current;
    const updatePrefixWidth = () => {
      setPrefixWidth(Math.ceil(element.getBoundingClientRect().width));
    };

    updatePrefixWidth();

    const resizeObserver = new ResizeObserver(updatePrefixWidth);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [suffix]);

  const finalSuffixPadding =
    prefixWidth !== null
      ? Math.max(MIN_SUFFIX_PADDING, PREFIX_INSET + prefixWidth + PREFIX_GAP)
      : (suffixPadding ??
        (suffix && typeof suffix === "string"
          ? Math.max(
              MIN_SUFFIX_PADDING,
              suffix.length * SUFFIX_PADDING_PER_CHARACTER,
            )
          : DEFAULT_SUFFIX_PADDING));

  const inputStyle: CSSProperties = {
    ...(rest.style as CSSProperties | undefined),
    paddingLeft: finalSuffixPadding,
  };

  const inputElement = (
    <Input
      type="number"
      step="any"
      min={0}
      className="border border-border text-sm"
      {...rest}
      onWheel={(e) => e.currentTarget.blur()}
      // In case we get value in localized form, we want to avoid commas as JS won't be able to parse those as numbers
      value={typeof value === "string" ? value.replace(/,/g, "") : value}
      {...(name && typeof formCtx.register === "function"
        ? formCtx.register(name, { valueAsNumber: true })
        : {})}
      style={inputStyle}
    />
  );

  return (
    <>
      <div className={cn("flex w-full items-center gap-x-2", className)}>
        <div className={cn("relative flex w-full items-center gap-x-2")}>
          {suffix && (
            <span
              ref={prefixRef}
              className={cn(
                `
                  pointer-events-none absolute inset-y-0 left-3 flex
                  items-center text-sm text-muted
                `,
                suffixClassName,
              )}
            >
              {suffix}
            </span>
          )}

          {name && formCtx.control ? (
            <Controller
              name={name}
              control={formCtx.control}
              render={() => inputElement}
            />
          ) : (
            inputElement
          )}
        </div>

        {onMaxSelected && (
          <div
            className="
              flex cursor-pointer self-center text-xs text-muted
              underline-offset-4
              hover:underline
            "
            onClick={onMaxSelected}
          >
            MAX
          </div>
        )}
      </div>
    </>
  );
};

export default CurrencyInput;
